import { useCallback } from 'react';

import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import type { PinType } from '../types';
import type { SearchWorkEntityType } from '../ui/search-work/types';

import { PINS_ANALYTICS } from './constants';

const PINS_EXPERIMENT = 'rovo_your_work_tab_pin_objects';

const isPinsExperimentEnabled = (): boolean => isExperimentEnabled(PINS_EXPERIMENT);

type SearchResolvedParams = {
	entityType: SearchWorkEntityType;
	queryLength: number;
	resultCount: number;
	outcome: 'success' | 'failure';
	duration: number;
};

type PinnedWorkSectionViewedParams = {
	state: 'empty' | 'populated';
	pinCount: number;
};

type PinMutationSucceededParams = {
	pinType: PinType;
	pinCount: number;
	duration: number;
};

type PinMutationFailedParams = {
	operation: 'pin' | 'unpin';
	reason: 'missingProduct' | 'itemNotFound' | 'requestFailed';
	pinType?: PinType;
	duration: number;
};

type PinsAnalytics = {
	fireSearchWorkViewed: () => void;
	firePinnedWorkSectionViewed: (params: PinnedWorkSectionViewedParams) => void;
	fireSearchWorkTabClicked: (entityType: SearchWorkEntityType) => void;
	fireWorkItemSearched: (params: SearchResolvedParams) => void;
	firePinnedItemAdded: (params: PinMutationSucceededParams) => void;
	firePinnedItemRemoved: (params: PinMutationSucceededParams) => void;
	firePinnedItemMutationFailed: (params: PinMutationFailedParams) => void;
};

export const usePinsAnalytics = (): PinsAnalytics => {
	const { sendAnalyticsEvent } = useAnalytics();

	const fireSearchWorkViewed = useCallback((): void => {
		if (!isPinsExperimentEnabled()) {
			return;
		}
		sendAnalyticsEvent({
			eventType: 'screen',
			name: PINS_ANALYTICS.screens.searchWork,
		});
	}, [sendAnalyticsEvent]);

	const firePinnedWorkSectionViewed = useCallback(
		({ state, pinCount }: PinnedWorkSectionViewedParams): void => {
			if (!isPinsExperimentEnabled()) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'ui',
				...PINS_ANALYTICS.events.pinnedWorkSectionViewed,
				attributes: { state, pinCount },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireSearchWorkTabClicked = useCallback(
		(entityType: SearchWorkEntityType): void => {
			if (!isPinsExperimentEnabled()) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'ui',
				...PINS_ANALYTICS.events.searchWorkTabClicked,
				attributes: { entityType },
			});
		},
		[sendAnalyticsEvent],
	);

	const fireWorkItemSearched = useCallback(
		({ entityType, queryLength, resultCount, outcome, duration }: SearchResolvedParams): void => {
			if (!isPinsExperimentEnabled()) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...PINS_ANALYTICS.events.workItemSearched,
				attributes: { entityType, queryLength, resultCount, outcome, duration },
			});
		},
		[sendAnalyticsEvent],
	);

	const firePinnedItemAdded = useCallback(
		({ pinType, pinCount, duration }: PinMutationSucceededParams): void => {
			if (!isPinsExperimentEnabled()) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...PINS_ANALYTICS.events.pinnedItemAdded,
				attributes: { pinType, pinCount, duration },
			});
		},
		[sendAnalyticsEvent],
	);

	const firePinnedItemRemoved = useCallback(
		({ pinType, pinCount, duration }: PinMutationSucceededParams): void => {
			if (!isPinsExperimentEnabled()) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'track',
				...PINS_ANALYTICS.events.pinnedItemRemoved,
				attributes: { pinType, pinCount, duration },
			});
		},
		[sendAnalyticsEvent],
	);

	const firePinnedItemMutationFailed = useCallback(
		({ operation, reason, pinType, duration }: PinMutationFailedParams): void => {
			if (!isPinsExperimentEnabled()) {
				return;
			}
			sendAnalyticsEvent({
				eventType: 'operational',
				...PINS_ANALYTICS.events.pinnedItemMutationFailed,
				attributes: { operation, reason, pinType, duration },
			});
		},
		[sendAnalyticsEvent],
	);

	return {
		fireSearchWorkViewed,
		firePinnedWorkSectionViewed,
		fireSearchWorkTabClicked,
		fireWorkItemSearched,
		firePinnedItemAdded,
		firePinnedItemRemoved,
		firePinnedItemMutationFailed,
	};
};
